import { YOU, type GlobeView } from "@/api/memory-focus";
import {
  chord,
  cross,
  fibonacciSphere,
  homeAngles,
  normalize,
  relaxOnSphere,
  type Angles,
  type Vec3,
} from "./globe-projection";
/** Past DENSE_AT drawn things the root label pass limits labels. SLOT_GAP and FRONT_CLEAR are chords
 * on the unit sphere: temporary spots stay that far from each other and from the point in front of You. */
export const [DRAWN_MAX, SLOT_MAX, DENSE_AT, SLOT_GAP, FRONT_CLEAR] = [
  60, 12, 40, 0.16, 0.22,
];
const SPOT_RINGS = [
  [0.3, 0.42, 0.56],
  [0.7, 0.84, 1],
  [1.2, 1.4, 1.6],
] as const;
export interface GlobeLayout {
  positions: ReadonlyMap<string, Vec3>;
  home: Angles;
}
/** Drawn things are kept clear where possible; other `spots` never come within SLOT_GAP, nor the `front` point within FRONT_CLEAR. */
export interface SpotRules {
  drawn: readonly Vec3[];
  spots?: readonly Vec3[];
  front?: Vec3 | null;
}
export function hashPoint(id: string): Vec3 {
  let h = 2166136261;
  for (let index = 0; index < id.length; index++)
    h = Math.imul(h ^ id.charCodeAt(index), 16777619);
  const z = (((h >>> 0) % 1000) / 1000) * 1.6 - 0.8;
  const theta = ((((h >>> 10) >>> 0) % 1000) / 1000) * Math.PI * 2;
  const r = Math.sqrt(1 - z * z);
  return [r * Math.cos(theta), z, r * Math.sin(theta)];
}
/** Positions for You's globe (mockup `computeSphere`): projects lead their orbit, each thing joins the best-ranked
 * project it links to, and springs along the facts pull linked things together. */
export function globeLayout(globe: GlobeView): GlobeLayout {
  const drawn = globe.things
    .map(({ thing }) => thing)
    .filter((thing) => thing.kind !== "user_profile" && thing.id)
    .slice(0, DRAWN_MAX);
  const ids = drawn.map((thing) => thing.id!);
  const index = new Map(ids.map((id, position) => [id, position]));
  const kinds = new Map(drawn.map((thing) => [thing.id!, thing.kind]));
  const neighbours = new Map(ids.map((id) => [id, [] as string[]]));
  const links: Array<[number, number]> = [];
  for (const fact of globe.facts) {
    const a = index.get(fact.source.id),
      b = index.get(fact.target.id);
    if (a === undefined || b === undefined || a === b) continue;
    links.push([a, b]);
    neighbours.get(fact.source.id)!.push(fact.target.id);
    neighbours.get(fact.target.id)!.push(fact.source.id);
  }
  const projects = ids.filter((id) => kinds.get(id) === "project");
  const orbit = (id: string) =>
    kinds.get(id) === "project"
      ? id
      : (neighbours
          .get(id)!
          .filter((other) => kinds.get(other) === "project")
          .sort((a, b) => index.get(a)! - index.get(b)!)[0] ?? YOU);
  const group = new Map(
    [...projects, YOU].map((id, position) => [id, position]),
  );
  const order = [...ids].sort(
    (a, b) =>
      group.get(orbit(a))! - group.get(orbit(b))! ||
      Number(kinds.get(a) !== "project") - Number(kinds.get(b) !== "project") ||
      index.get(a)! - index.get(b)!,
  );
  const sphere = fibonacciSphere(order.length);
  const start: Vec3[] = new Array(order.length);
  order.forEach((id, position) => {
    start[index.get(id)!] = sphere[position]!;
  });
  const relaxed = relaxOnSphere(start, links);
  const positions = new Map(
    ids.map((id, position) => [id, relaxed[position]!]),
  );
  return {
    positions,
    home: homeAngles(ids[0] ? positions.get(ids[0])! : null),
  };
}
/** Spots near an anchor for things that are not drawn (mockup `placeTemps`): the clearest candidate on rings 0.3–0.56
 * rad out (then wider rings), never within SLOT_GAP of the anchor or another spot, nor FRONT_CLEAR of the front point. */
export function placeTemps(
  anchor: Vec3,
  count: number,
  { drawn, spots = [], front = null }: SpotRules,
): Vec3[] {
  const e1 = normalize(
    cross(Math.abs(anchor[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0], anchor),
  );
  const e2 = cross(anchor, e1);
  const placed: Vec3[] = [];
  for (let n = 0; n < count; n++) {
    const hard = [anchor, ...spots, ...placed];
    const all = [...hard, ...drawn, ...(front ? [front] : [])];
    let best: Vec3 | null = null;
    for (let ring = 0; best === null && ring < SPOT_RINGS.length; ring++) {
      let score = -Infinity;
      for (const rho of SPOT_RINGS[ring]!) {
        for (let k = 0; k < 36; k++) {
          const phi = (k / 36) * Math.PI * 2;
          const [c, s, cp, sp] = [
            Math.cos(rho),
            Math.sin(rho),
            Math.cos(phi),
            Math.sin(phi),
          ];
          const p: Vec3 = [
            c * anchor[0] + s * (cp * e1[0] + sp * e2[0]),
            c * anchor[1] + s * (cp * e1[1] + sp * e2[1]),
            c * anchor[2] + s * (cp * e1[2] + sp * e2[2]),
          ];
          if (
            hard.some((q) => chord(p, q) < SLOT_GAP) ||
            (front && chord(p, front) < FRONT_CLEAR)
          )
            continue;
          const clearance =
            Math.min(0.3, ...all.map((q) => chord(p, q))) - rho * 0.12;
          if (clearance > score) {
            score = clearance;
            best = p;
          }
        }
      }
    }
    placed.push(best ?? hashPoint(`${anchor.join(",")}:${n}`));
  }
  return placed;
}
